import { AfterContentInit, Component, ContentChild, ContentChildren, ElementRef, EventEmitter, forwardRef, HostBinding, HostListener, Inject, Input, Output, QueryList, ViewEncapsulation, Renderer2, ViewChild } from "@angular/core";
import { MatButton, MatMiniFabButton } from '@angular/material/button';


@Component({
    selector: "smd-fab-actions",
    template: `
    <div #actionsWrapper class="smd-actions-wrapper">
      <ng-content></ng-content>
    </div>
  `,
    standalone: false
})
export class SmdFabSpeedDialActionsComponent implements AfterContentInit
{
	@ViewChild('actionsWrapper', { static: true })
	private actionsWrapper!: ElementRef;

	private _buttons: HTMLElement[] = [];

	public Z_INDEX_ITEM = 23;

	constructor(
		@Inject(forwardRef(() => SmdFabSpeedDialComponent))
		private _parent: SmdFabSpeedDialComponent,
		private renderer: Renderer2
	) { }

	ngAfterContentInit(): void
	{
		this.updateButtons();

		// Se il contenuto cambia dinamicamente
		setTimeout(() => this.updateButtons());
	}

	private updateButtons(): void
	{
		this._buttons = Array.from(
			this.actionsWrapper.nativeElement.querySelectorAll(
				'button[mat-mini-fab]'
			)
		) as HTMLElement[];

		this.initButtonStates();
	}

	private initButtonStates(): void
	{
		this._buttons.forEach((button, i) =>
		{
			this.renderer.addClass(button, "smd-fab-action-item");

			this.renderer.setStyle(
				button,
				"z-index",
				`${this.Z_INDEX_ITEM - i}`
			);
		});
	}

	show(): void
	{
		this._buttons.forEach((button, i) =>
		{
			let transitionDelay = 0;
			let transform: string;

			if (this._parent.animationMode === "scale")
			{
				transitionDelay = 3 + 65 * i;
				transform = "scale(1)";
			} else
			{
				transform = this.getTranslateFunction("0");
			}

			this.renderer.setStyle(button, "transition-delay", `${transitionDelay}ms`);
			this.renderer.setStyle(button, "opacity", "1");
			this.renderer.setStyle(button, "transform", transform);
		});
	}

	hide(): void
	{
		this._buttons.forEach((button, i) =>
		{
			let opacity = "1";
			let transitionDelay = 0;
			let transform: string;

			if (this._parent.animationMode === "scale")
			{
				transitionDelay = 3 - 65 * i;
				transform = "scale(0)";
				opacity = "0";
			} else
			{
				transform = this.getTranslateFunction(
					55 * (i + 1) - i * 5 + "px"
				);
			}

			this.renderer.setStyle(button, "transition-delay", `${transitionDelay}ms`);
			this.renderer.setStyle(button, "opacity", opacity);
			this.renderer.setStyle(button, "transform", transform);
		});
	}

	private getTranslateFunction(value: string): string
	{
		const dir = this._parent.direction;
		const translateFn =
			dir === "up" || dir === "down" ? "translateY" : "translateX";
		const sign = dir === "down" || dir === "right" ? "-" : "";
		return `${translateFn}(${sign}${value})`;
	}
}

@Component({
    selector: "smd-fab-trigger",
    template: `<ng-content select="[md-fab], [mat-fab]"></ng-content>`,
    standalone: false
})
export class SmdFabSpeedDialTriggerComponent
{
	// I/O
	@Input() spin = false;

	// constructor
	constructor(@Inject(forwardRef(() => SmdFabSpeedDialComponent))
	private _parent: SmdFabSpeedDialComponent)
	{
	}

	// get spin
	@HostBinding("class.smd-spin")
	get sp()
	{
		return this.spin;
	}

	// on click
	@HostListener("click", ["$event"])
	_onClick(event: any)
	{
		if (!this._parent.fixed)
		{
			this._parent.toggle();
			event.stopPropagation();
		}
	}
}

@Component({
    selector: "smd-fab-speed-dial",
    template: `
        <div class="smd-fab-speed-dial-container">
            <ng-content select="smd-fab-trigger"></ng-content>
            <ng-content select="smd-fab-actions"></ng-content>
        </div>
    `,
    styleUrls: ["fab.component.scss"],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class SmdFabSpeedDialComponent implements AfterContentInit
{
	// viewchildren
	@ContentChild(SmdFabSpeedDialActionsComponent) _childActions: SmdFabSpeedDialActionsComponent;

	// I/O
	@Input() fixed = false;

	@HostBinding("class.smd-opened")
	@Input()
	get open()
	{
		return this._open;
	}
	set open(open: boolean)
	{
		const previousOpen = this._open;
		this._open = open;
		if (previousOpen !== this._open)
		{
			this.openChange.emit(this._open);
			if (this.isInitialized)
			{
				this.setActionsVisibility();
			}
		}
	}

	@Input()
	get direction()
	{
		return this._direction;
	}
	set direction(direction: string)
	{
		const previousDir = this._direction;
		this._direction = direction;
		if (previousDir !== this.direction)
		{
			this._setElementClass(previousDir, false);
			this._setElementClass(this.direction, true);

			if (this.isInitialized)
			{
				this.setActionsVisibility();
			}
		}
	}

	@Input()
	get animationMode()
	{
		return this._animationMode;
	}
	set animationMode(animationMode: string)
	{
		const previousAnimationMode = this._animationMode;
		this._animationMode = animationMode;
		if (previousAnimationMode !== this._animationMode)
		{
			this._setElementClass(previousAnimationMode, false);
			this._setElementClass(this.animationMode, true);

			if (this.isInitialized)
			{
				// To start another detect lifecycle and force the "close" on the action buttons
				Promise.resolve(null).then(() => (this.open = false));
			}
		}
	}

	@Output() openChange: EventEmitter<boolean> = new EventEmitter<boolean>();

	// variables
	private isInitialized = false;
	private _direction = "up";
	private _open = false;
	private _animationMode = "fling";

	// constructor
	constructor(private elementRef: ElementRef, private renderer: Renderer2)
	{
	}

	// after content init
	ngAfterContentInit(): void
	{
		this.isInitialized = true;
		this.setActionsVisibility();
		this._setElementClass(this.direction, true);
		this._setElementClass(this.animationMode, true);
	}

	// toggle
	public toggle()
	{
		this.open = !this.open;
	}

	// on click
	@HostListener("click")
	_onClick()
	{
		if (!this.fixed && this.open)
		{
			this.open = false;
		}
	}

	// set action visibility
	setActionsVisibility()
	{
		if (this.open)
		{
			this._childActions.show();
		} else
		{
			this._childActions.hide();
		}
	}

	// set element class
	private _setElementClass(elemClass: string, isAdd: boolean)
	{
		isAdd ? this.renderer.addClass(this.elementRef.nativeElement, `smd-${elemClass}`) : this.renderer.removeClass(this.elementRef.nativeElement, `smd-${elemClass}`);
	}
}
